import React, { useState } from 'react';
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import { api } from '../services/api';
import ReactMarkdown from 'react-markdown';
import {
  Box, Container, Card, CardContent, Button, Typography, Checkbox,
  FormControlLabel, Alert, CircularProgress, Divider
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';

function RulesAgreement() {
  const location = useLocation();
  const navigate = useNavigate();
  const { programId, programName, programRules, enrollments } = location.state || {};
  const [agreed, setAgreed] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [error, setError] = useState('');

  if (!programId || !enrollments || enrollments.length === 0) {
    navigate('/programs');
    return null;
  }

  const handleAgree = async () => {
    if (!agreed) { setError('Please agree to the program rules before continuing.'); return; }
    try {
      setEnrolling(true);
      setError('');
      const response = await api.enrollInProgram({
        program_id: programId,
        enrollees: enrollments.map((e) => ({
          enrollee_id: e.enrollee_id,
          custom_responses: e.custom_responses || {}
        }))
      });
      navigate('/checkout', { state: { clientSecret: response.client_secret } });
    } catch (err) {
      console.error('Error enrolling:', err);
      setError(err.response?.data?.error || 'Failed to enroll. Please try again.');
    } finally {
      setEnrolling(false);
    }
  };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', pt: 12, pb: 4 }}>
      <Container maxWidth="md">
        <Button component={RouterLink} to={`/programs/${programId}`} startIcon={<ArrowBackIcon />} sx={{ mb: 3 }}>
          Back to Program
        </Button>

        <Card>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" component="h2" sx={{ fontWeight: 700, mb: 1 }}>Program Rules Agreement</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>{programName}</Typography>
            <Divider sx={{ mb: 3 }} />

            <Alert severity="info" sx={{ mb: 3 }}>
              Please read the following rules carefully. You must agree to these rules to enroll your child(ren) in this program.
            </Alert>

            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Enrolling:</Typography>
            <Box sx={{ mb: 3 }}>
              {enrollments.map((e) => (
                <Typography key={e.enrollee_id} variant="body1" sx={{ mb: 0.5 }}>• {e.name}</Typography>
              ))}
            </Box>

            <Divider sx={{ mb: 3 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Program Rules</Typography>
            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
            <Box sx={{ mb: 3, '& ol': { pl: 3 }, '& li': { mb: 2, lineHeight: 1.8 } }}>
              <ReactMarkdown>{programRules || ''}</ReactMarkdown>
            </Box>

            <Divider sx={{ mb: 3 }} />
            <FormControlLabel
              control={<Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)} disabled={enrolling} />}
              label={<Typography variant="body1" sx={{ fontWeight: 500 }}>I have read and agree to abide by all program rules listed above</Typography>}
              sx={{ mb: 3 }}
            />

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button component={RouterLink} to={`/programs/${programId}`} variant="outlined" disabled={enrolling}>Cancel</Button>
              <Button variant="contained" onClick={handleAgree} disabled={!agreed || enrolling}
                startIcon={enrolling ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}>
                {enrolling ? 'Processing...' : 'Agree and Continue to Checkout'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}

export default RulesAgreement;
